<template>
	<BasePlaceHolder class="advisor-creation-form" :placehold="loading" :line-count="3">
		<form @submit.prevent>
			<div class="grid-md gutters-150-md mb-150">
				<div class="col-6-md">
					<InputField :field="v$.firstName" field-name="First Name">
						<template #label>First name</template>
						<template #input>
							<input
								v-model="v$.firstName.$model"
								data-test="first-name"
								name="firstName"
								class="input"
								type="text"
							/>
						</template>
					</InputField>
				</div>
				<div class="col-6-md mt-100-smo">
					<InputField :field="v$.lastName" field-name="Last Name">
						<template #label>Last name</template>
						<template #input>
							<input
								v-model="v$.lastName.$model"
								data-test="last-name"
								name="lastName"
								class="input"
								type="text"
							/>
						</template>
					</InputField>
				</div>
			</div>
			<div class="grid-md gutters-150-md mb-150">
				<div class="col-6-md">
					<InputField :field="v$.phoneNumber" field-name="Phone Number">
						<template #label>Phone number (mobile preferred)</template>
						<template #input>
							<PhoneInput v-model="v$.phoneNumber.$model" data-test="phone-number" name="phoneNumber" />
						</template>
					</InputField>
				</div>
				<div class="col-6-md mt-100-smo">
					<InputField :field="v$.approximateAum" field-name="Approximate AUM">
						<template #label>Approximate AUM</template>
						<template #input>
							<BaseSelect
								v-model="v$.approximateAum.$model"
								component-id="approximate-aum"
								:options="approximateAumOptions"
							/>
						</template>
					</InputField>
				</div>
			</div>
			<InputField :field="v$.companyName" field-name="Company Name">
				<template #label>Company name</template>
				<template #input>
					<input
						v-model="v$.companyName.$model"
						data-test="company-name"
						name="companyName"
						class="input"
						type="text"
					/>
				</template>
			</InputField>

			<InputField
				class="my-100"
				:field="v$.email"
				field-name="Email Address"
				@clear-server="serverValidationHandler.clearServerErrors('email')"
			>
				<template #label>Email address</template>
				<template #input>
					<input v-model="v$.email.$model" data-test="email" name="email" class="input" type="email" />
				</template>
			</InputField>

			<InputField
				class="my-100"
				:field="v$.password"
				field-name="Password"
				@clear-server="serverValidationHandler.clearServerErrors('password')"
			>
				<template #label>Create password</template>
				<template #input>
					<div class="display-flex align-items-center position-relative">
						<input
							v-model="v$.password.$model"
							data-test="password"
							name="password"
							class="input"
							:type="maskedPassword ? 'password' : 'text'"
						/>
						<BaseSvgIcon
							class="advisor-creation-form__password-icon position-absolute"
							data-test="mask-password-icon"
							width="23"
							height="23"
							:name="maskedPassword ? 'view' : 'view-hide'"
							@click="maskedPassword = !maskedPassword"
						/>
					</div>
				</template>
			</InputField>
			<p class="epsilon my-150">
				<span v-for="(link, index) in clientAgreementLinks" :key="`link.title${index}`">
					{{ link.prelude }}
					<BaseLink
						:link="{ target: '_blank', href: link.url, text: link.title }"
						class="'text-color-current-color text-decoration-underline'"
					></BaseLink> </span
				>.
			</p>
			<BaseButtonGroup>
				<template #primary>
					<SubmitButton
						:is-working="isSubmitted"
						:v$="v$"
						data-test="advisor-creation-submit"
						@sb-click="submit"
						>Create account
					</SubmitButton>
				</template>
			</BaseButtonGroup>
		</form>
	</BasePlaceHolder>
</template>

<script setup lang="ts">
import { AdvisorSignupForm, ApproximateAum } from 'types/advisor';
import { computed, onMounted, reactive, ref, Ref } from 'vue';
import { email, maxLength, minLength, numeric, required } from '@vuelidate/validators';
import { getDistinctId, trackSignupEvents } from '@utils/analytics';
import { createAdvisorUser } from '@api/advisor';
import { DocumentListItem } from 'types/documents';
import { getFundriseClientAgreementDocuments } from '@api/documents';
import { getItemFromLocalStorage } from '@utils/web-storage';
import InputField from '@components/form/input-field.vue';
import { nameRegex } from '@validators/nameRegex';
import { passwordMaxLength } from '@constants/validation';
import { passwordRegex } from '@validators/passwordRegex';
import PhoneInput from '@components/form/phone-input.vue';
import ServerValidationHandler from '@classes/ServerValidationHandler';
import SubmitButton from '@components/form/submit-button.vue';
import { useAppStore } from '@stores/app';
import { useRouter } from 'vue-router';
import { useUserStore } from '@stores/user';
import { useVuelidate } from '@vuelidate/core';
import { valueSelected } from '@validators/valueSelected';

interface ApproximateAumOption {
	label: string;
	value: ApproximateAum | '-1';
}

const router = useRouter();

const appStore = useAppStore();
const userStore = useUserStore();

const serverValidationHandler = ref(new ServerValidationHandler());

interface ComponentState {
	firstName: string;
	lastName: string;
	phoneNumber: string;
	companyName: string;
	email: string;
	approximateAum: ApproximateAumOption;
	password: string;
}
const state: ComponentState = reactive({
	firstName: '',
	lastName: '',
	phoneNumber: '',
	companyName: '',
	email: '',
	approximateAum: {
		label: 'Select one',
		value: '-1'
	},
	password: ''
});
const rules = computed(() => ({
	firstName: {
		required,
		nameRegex,
		serverError: serverValidationHandler.value.serverError('firstName')
	},
	lastName: {
		required,
		nameRegex,
		serverError: serverValidationHandler.value.serverError('lastName')
	},
	phoneNumber: {
		required,
		numeric,
		minLength: minLength(10),
		serverError: serverValidationHandler.value.serverError('phoneNumber')
	},
	companyName: {
		required,
		serverError: serverValidationHandler.value.serverError('companyName')
	},
	email: {
		required,
		email,
		serverError: serverValidationHandler.value.serverError('email')
	},
	approximateAum: {
		required: valueSelected('Select one', 'label'),
		serverError: serverValidationHandler.value.serverError('approximateAum')
	},
	password: {
		required,
		maxLength: maxLength(passwordMaxLength),
		passwordRegex: passwordRegex(),
		serverError: serverValidationHandler.value.serverError('password')
	}
}));
const v$ = useVuelidate(rules, state);

const loading = ref(true);
const isSubmitted = ref(false);
const maskedPassword = ref(true);

const clientAgreementLinks: Ref<Array<DocumentListItem & { prelude: string }>> = ref([]);

const advisorSignupForm: Ref<AdvisorSignupForm> = ref({
	firstName: '',
	lastName: '',
	email: '',
	mixpanelId: '',
	password: '',
	companyName: '',
	approximateAum: '' as ApproximateAum,
	termsAcknowledgedVersionId: '',
	privacyPolicyAcknowledgedVersionId: '',
	clientId: `${import.meta.env.VITE_API_CLIENT_ID}`,
	phoneNumber: ''
});

const approximateAumOptions: ApproximateAumOption[] = [
	{
		label: 'Select one',
		value: '-1'
	},
	{
		label: 'Less than $50 million',
		value: 'ZERO_TO_FIFTY_MILLION'
	},
	{
		label: '$50 million - $100 million',
		value: 'FIFTY_MILLION_TO_ONE_HUNDRED_MILLION'
	},
	{
		label: '$100 million - $500 million',
		value: 'ONE_HUNDRED_MILLION_TO_FIVE_HUNDRED_MILLION'
	},
	{
		label: '$500 million - $1 billion',
		value: 'FIVE_HUNDRED_MILLION_TO_ONE_BILLION'
	},
	{
		label: '$1 billion or more',
		value: 'OVER_ONE_BILLION'
	}
];

onMounted(() => {
	if (getItemFromLocalStorage('emailcapture')) {
		state.email = getItemFromLocalStorage('emailcapture') || '';
		v$.value.email.$touch();
	}
});

async function submit(): Promise<void> {
	isSubmitted.value = true;
	maskedPassword.value = true;

	attemptAdvisorCreation();
}

async function attemptAdvisorCreation(): Promise<void> {
	const mixpanelId = getDistinctId() ?? '';
	const userForm = { ...advisorSignupForm.value };

	userForm.firstName = state.firstName;
	userForm.lastName = state.lastName;
	userForm.email = state.email;
	userForm.password = state.password;
	userForm.companyName = state.companyName;
	userForm.approximateAum = state.approximateAum.value as ApproximateAum;
	userForm.phoneNumber = state.phoneNumber;
	userForm.mixpanelId = mixpanelId;

	try {
		const response = await createAdvisorUser(userForm);
		const { accessToken, ...userData } = response;
		appStore.setUserAuthData(accessToken);
		userStore.user = userData;
		trackSignupEvents();

		await router.push({ name: 'advisor-unverified-email' });
	} catch (e) {
		serverValidationHandler.value.handleServerValidation(e);
	}

	isSubmitted.value = false;
}

async function setup(): Promise<void> {
	const clientAgreementDocuments = await getFundriseClientAgreementDocuments();
	advisorSignupForm.value.privacyPolicyAcknowledgedVersionId =
		clientAgreementDocuments.privacyPolicy.documentRevisionId;
	advisorSignupForm.value.termsAcknowledgedVersionId = clientAgreementDocuments.termsOfService.documentRevisionId;

	clientAgreementLinks.value = [
		{
			prelude: 'By continuing you indicate you have reviewed and agree to the',
			...clientAgreementDocuments.termsOfService
		},
		{ prelude: ' and ', ...clientAgreementDocuments.privacyPolicy },
		{ prelude: `, and have received a copy of Fundrise Advisors `, ...clientAgreementDocuments.formAdvPart2 },
		{ prelude: ', Fundrise Advisors ', ...clientAgreementDocuments.formCrs },
		{
			prelude: ', and Fundrise Advisors ',
			...clientAgreementDocuments.clientAgreement
		}
	];
	loading.value = false;
}
setup();
</script>

<style lang="scss">
.advisor-creation-form {
	&__password-icon {
		right: 13px;

		&:hover,
		&:active {
			cursor: pointer;
		}
	}
}
</style>
